import {useEffect, useRef, useState} from 'react';
import {Button} from 'primereact/button';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dialog} from 'primereact/dialog';
import {Fieldset} from 'primereact/fieldset';
import {Toast} from 'primereact/toast';
import {useDataTable} from '@iamsoftware/react-hooks';
import {saveAs} from 'file-saver';
import {PitAgreementService} from './PitAgreementService';

const statusText: Record<string, string> = {
  AgrtCreated: 'Mới tạo', AgrtApproved: 'Chờ ký', AgrtSigning: 'Đang ký',
  AgrtSignedByPartyA: 'Khách hàng đã ký', AgrtSignedByPartyB: 'Đơn vị cung cấp đã ký',
  AgrtSignedCompleted: 'Đã ký hoàn tất', AgrtCancelled: 'Đã hủy'
};

function pdfBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], {type: 'application/pdf'});
}

function dateText(value: string | number | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('vi-VN');
}

function moneyText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toLocaleString('vi-VN') : '-';
}

function lineTotal(row: any): number {
  return Number(row.quantity || 0) * Number(row.unitAmount || 0) + Number(row.taxAmount || 0);
}

function ReadOnlyField({label, value, className = 'col-12 md:col-6'}: {label: string; value: unknown; className?: string}) {
  return <div className={className}>
    <div className="text-600 text-sm mb-1">{label}</div>
    <div className="border-1 surface-border border-round p-2" style={{minHeight: '2.5rem', overflowWrap: 'anywhere'}}>
      {value === null || value === undefined || value === '' ? '-' : String(value)}
    </div>
  </div>;
}

export const PitAgreements = () => {
  const [selected, setSelected] = useState<any>(null);
  const [pdf, setPdf] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const toast = useRef<Toast>(null);
  const showError = (error: any, fallback: string) => toast.current?.show({
    severity: 'error', summary: 'Lỗi', detail: error?.response?.data?.errors || error?.message || fallback
  });
  useEffect(() => () => { if (pdf) URL.revokeObjectURL(pdf); }, [pdf]);

  const openDetail = async (id: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try { setSelected((await PitAgreementService.detail(id)).agreementOut); }
    catch (error: any) { showError(error, 'Không tải được chi tiết'); }
    finally { busyRef.current = false; setBusy(false); }
  };

  const openPdf = async () => {
    if (!selected || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try { setPdf(URL.createObjectURL(pdfBlob((await PitAgreementService.pdf(selected.agreementId)).pdfBase64))); }
    catch (error: any) { showError(error, 'Không tải được PDF'); }
    finally { busyRef.current = false; setBusy(false); }
  };

  const downloadPdf = async (row: any) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const {pdfBase64} = await PitAgreementService.pdf(row.agreementId);
      saveAs(pdfBlob(pdfBase64), `hop-dong-${row.agreementId}.pdf`);
    } catch (error: any) { showError(error, 'Không tải được PDF'); }
    finally { busyRef.current = false; setBusy(false); }
  };

  const sign = async (agreementId: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    let token = '';
    try {
      const detail = (await PitAgreementService.detail(agreementId)).agreementOut;
      if (!detail?.canSign) throw new Error('Hợp đồng không còn ở trạng thái có thể ký');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 1500);
      let test: any;
      try { test = await fetch('http://localhost:13369/test', {signal: controller.signal}).then(res => res.json()); }
      catch { throw new Error('Không tìm thấy Signing Tool. Vui lòng mở ứng dụng ký số rồi thử lại'); }
      finally { clearTimeout(timer); }
      if (test?.receiveDataFromBrowser !== 'Y') throw new Error('Cần mở hoặc cập nhật Signing Tool trước khi ký');
      const prepared = await PitAgreementService.prepare(agreementId);
      token = prepared.signingToken;
      const response = await fetch('http://localhost:13369/signing', {
        method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(prepared)
      });
      const signed = await response.json();
      const xml = signed.xmlSignedBase64 || signed.base64FileData;
      if (!response.ok || !signed.success || !xml) throw new Error(signed.errorMessage || 'Signing Tool không trả XML đã ký');
      const completed = await PitAgreementService.complete(agreementId, token, xml);
      if (completed?.isSuccess !== 'Y') throw new Error('Chưa hoàn tất ký hợp đồng');
      token = '';
      reloadLazyData();
      toast.current?.show({severity: 'success', summary: 'Thành công', detail: 'Đã ký hợp đồng'});
      if (selected?.agreementId === agreementId) {
        PitAgreementService.detail(agreementId).then(result => setSelected(result.agreementOut)).catch(() => setSelected(null));
      }
    } catch (error: any) {
      if (token) await PitAgreementService.fail(agreementId, token).catch(() => undefined);
      showError(error, 'Không thể ký hợp đồng');
    } finally { busyRef.current = false; setBusy(false); }
  };

  const {render: renderDataTable, reloadLazyData} = useDataTable({
    stateKey: 'pitAgreementsTableFilters',
    tableHeader: <div className="flex justify-content-between align-items-center">
      <span>Danh sách hợp đồng chứng từ TNCN</span>
      <Button icon="pi pi-refresh" aria-label="Tải lại" title="Tải lại" className="p-button-text" onClick={() => reloadLazyData()}/>
    </div>,
    dataKey: 'agreementId',
    columns: [
      {field: 'agreementId', header: 'Mã hợp đồng', width: 115, matchMode: 'contains'},
      {field: 'agreementTypeEnum', header: 'Loại hợp đồng', width: 145, matchMode: 'contains'},
      {field: 'agreementNo', header: 'Số hợp đồng', minWidth: 190, matchMode: 'contains'},
      {field: 'statusId', header: 'Trạng thái', width: 155, dataType: 'custom', filterType: 'dropdown', matchMode: 'equals',
        filterOptions: Object.entries(statusText).map(([value, label]) => ({value, label})),
        customCell: (row: any) => statusText[row.statusId] || row.status || row.statusId || '-'},
      {field: 'agreementDate', header: 'Ngày tạo', width: 125, dataType: 'date', filterType: 'date', matchMode: 'byDate'},
      {field: 'otherPartyName', header: 'Tên bên mua', minWidth: 220, matchMode: 'contains'},
      {field: 'otherPartyTaxId', header: 'MST bên mua', width: 145, matchMode: 'contains'},
      {field: 'otherRepName', header: 'Người đại diện bên mua', minWidth: 175, matchMode: 'contains'},
      {field: 'organizationPartyName', header: 'Tên bên bán', minWidth: 220, matchMode: 'contains'},
      {field: 'organizationPartyTaxId', header: 'MST bên bán', width: 145, matchMode: 'contains'}
    ],
    indexColumnWidth: 45,
    actionColumnWidth: 150,
    initActions(rows: any[]) {
      rows?.forEach(row => {
        row.actions = [
          {icon: 'pi pi-eye', title: 'Xem chi tiết', className: 'p-button-info', command: () => openDetail(row.agreementId)},
          {icon: 'pi pi-file-pdf', title: 'Tải PDF', className: 'p-button-success', command: () => downloadPdf(row)},
          {icon: 'pi pi-pencil', title: 'Ký điện tử', className: 'p-button-primary',
            disabled: !['AgrtApproved', 'AgrtSignedByPartyA', 'AgrtSigning'].includes(row.statusId),
            command: () => sign(row.agreementId)}
        ];
      });
    },
    getList: (lazyLoadEvent: string) => PitAgreementService.adminList(lazyLoadEvent).catch((error: any) => {
      showError(error, 'Không tải được danh sách hợp đồng');
      return {listData: [], totalRecords: 0};
    })
  });

  return <div className="grid">
    <Toast ref={toast}/>
    <div className="col-12 py-0" style={{maxHeight: 'calc(100vh - 12rem)'}}>{renderDataTable()}</div>
    <Dialog header="Xem nhanh hợp đồng" visible={!!selected} onHide={() => setSelected(null)}
            style={{width: 'min(980px, 96vw)', maxHeight: '92vh'}}
            contentStyle={{overflowY: 'auto'}}
            footer={selected && <div className="flex justify-content-end flex-wrap gap-2">
              <Button label="Xem PDF" icon="pi pi-file-pdf" disabled={busy} onClick={openPdf}/>
              <Button label="Tải PDF" icon="pi pi-download" disabled={busy} onClick={() => downloadPdf(selected)}/>
              {selected.canSign && <Button label="Ký USB Token" icon="pi pi-pencil" disabled={busy}
                                            onClick={() => sign(selected.agreementId)}/>}
              <Button label="Đóng" icon="pi pi-times" outlined disabled={busy} onClick={() => setSelected(null)}/>
            </div>}>
      {selected && <div>
        <div className="grid">
          <ReadOnlyField label="Mã hợp đồng" value={selected.agreementId} className="col-12 md:col-3"/>
          <ReadOnlyField label="Loại hợp đồng" value={selected.agreementTypeEnum || selected.agreementTypeEnumId} className="col-12 md:col-3"/>
          <ReadOnlyField label="Số hợp đồng" value={selected.agreementNo} className="col-12 md:col-3"/>
          <ReadOnlyField label="Ngày tạo" value={dateText(selected.agreementDate)} className="col-12 md:col-3"/>
          <ReadOnlyField label="Trạng thái" value={statusText[selected.statusId] || selected.status || selected.statusId} className="col-12 md:col-3"/>
        </div>
        <Fieldset legend="Bên bán" className="mt-3">
          <div className="grid">
            <ReadOnlyField label="Tên" value={selected.organizationPartyName}/>
            <ReadOnlyField label="Mã số thuế" value={selected.organizationPartyTaxId}/>
            <ReadOnlyField label="Địa chỉ" value={selected.organizationPartyAddr} className="col-12"/>
          </div>
        </Fieldset>
        <Fieldset legend="Bên mua" className="mt-3">
          <div className="grid">
            <ReadOnlyField label="Tên" value={selected.otherPartyName}/>
            <ReadOnlyField label="Mã số thuế" value={selected.otherPartyTaxId}/>
            <ReadOnlyField label="Địa chỉ" value={selected.otherPartyAddr} className="col-12"/>
            <ReadOnlyField label="Người đại diện" value={selected.otherRepName}/>
            <ReadOnlyField label="Chức vụ" value={selected.otherRepJobTitle}/>
            <ReadOnlyField label="Email người đại diện" value={selected.otherRepEmail}/>
            <ReadOnlyField label="Số điện thoại người đại diện" value={selected.otherRepPhoneNumber}/>
          </div>
        </Fieldset>
        <Fieldset legend="Gói dịch vụ" className="mt-3">
          <DataTable value={selected.packageItems || selected.items || []} size="small" showGridlines stripedRows
                     responsiveLayout="scroll" emptyMessage="Không có gói dịch vụ">
            <Column header="#" body={(_row: any, options: any) => options.rowIndex + 1} style={{width: '3rem'}}/>
            <Column field="pseudoId" header="Mã gói" body={(row: any) => row.pseudoId || '-'}/>
            <Column field="itemText" header="Tên gói dịch vụ"/>
            <Column field="unitAmount" header="Đơn giá" body={(row: any) => moneyText(row.unitAmount)}/>
            <Column field="quantity" header="Số lượng" body={(row: any) => moneyText(row.quantity)}/>
          </DataTable>
          <div className="text-right mt-2"><strong>Tổng tiền gói: {moneyText((selected.packageItems || selected.items || []).reduce(
            (total: number, row: any) => total + lineTotal(row), 0))}</strong></div>
        </Fieldset>
        {!!selected.fees?.length && <Fieldset legend="Các loại phí" className="mt-3">
          <DataTable value={selected.fees} size="small" showGridlines stripedRows responsiveLayout="scroll">
            <Column header="#" body={(_row: any, options: any) => options.rowIndex + 1} style={{width: '3rem'}}/>
            <Column field="itemText" header="Mô tả"/>
            <Column field="quantityUomDesc" header="Đơn vị tính" body={(row: any) => row.quantityUomDesc || '-'}/>
            <Column header="Số tiền" body={(row: any) => moneyText(lineTotal(row))}/>
          </DataTable>
        </Fieldset>}
        <div className="text-right mt-3"><strong>Tổng tiền hợp đồng: {moneyText(selected.agreementTotal)}</strong></div>
      </div>}
    </Dialog>
    <Dialog header="PDF hợp đồng" visible={!!pdf} onHide={() => setPdf('')} style={{width: 'min(1100px, 96vw)'}}>
      {pdf && <iframe title="PDF hợp đồng" src={pdf} style={{width: '100%', height: '75vh', border: 0}}/>}
    </Dialog>
  </div>;
};
